import { useMemo } from 'react'

import { ChartBar } from '@gravity-ui/icons'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/ui/EmptyState'
import { useAnalyticsStore } from '@/store/analytics.store'
import { useThemeStore } from '@/store/theme.store'

export function AnalyticsBarChart() {
  const { t } = useTranslation()
  const { reportItems, filters } = useAnalyticsStore()
  const isDark = useThemeStore(s => s.dark)

  const options = useMemo<Highcharts.Options>(() => {
    if (!filters.from || !filters.to || reportItems.length === 0) return {}

    const typeTotals = new Map<string, number>()

    reportItems.forEach(item => {
      const type = item.type || 'Unknown'
      const current = typeTotals.get(type) || 0
      typeTotals.set(type, current + (item.amountPLN || 0))
    })

    const sortedData = Array.from(typeTotals.entries())
      .map(([type, amount]) => ({ type, amount }))
      .sort((a, b) => b.amount - a.amount)

    const categories = sortedData.map(d => d.type)
    const data = sortedData.map(d => Number(d.amount.toFixed(2)))

    const textColor = isDark ? '#e4e4e7' : '#27272a'

    return {
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
      },
      title: { text: '' },
      xAxis: {
        categories,
        labels: { style: { color: textColor } },
        lineColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        tickColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
      yAxis: {
        title: { text: 'PLN', style: { color: textColor } },
        labels: { style: { color: textColor } },
        gridLineColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            format: '{point.y} PLN',
            style: {
              color: textColor,
              textOutline: 'none',
              fontWeight: 'normal',
            }
          }
        },
        series: {
          color: isDark ? '#3b82f6' : '#2563eb', // blue-500 or blue-600
        }
      },
      series: [
        {
          name: t('analytics.expenses_by_type', 'Expenses by Type'),
          data,
          type: 'bar',
        }
      ],
      credits: { enabled: false },
      tooltip: {
        valueSuffix: ' PLN'
      }
    }
  }, [reportItems, filters, isDark, t])

  if (reportItems.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <EmptyState
          icon={<ChartBar className="size-8" />}
          title={t('analytics.no_expenses_period', { defaultValue: 'No expenses for this period' })} description={''}
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full">
      <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { height: '100%', minHeight: '300px' } }} />
    </div>
  )
}
